import React, { useContext, useState, useEffect } from 'react';
import { LanguageContext } from '../App';
import Layout from './Layout';
import axios from 'axios';

const Calendar = () => {
  const { t, API } = useContext(LanguageContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarTasks, setCalendarTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);

  const monthNames = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

  const fetchCalendarTasks = async (year, month) => {
    try {
      const response = await axios.get(`${API}/tasks/calendar/${year}/${month}`);
      setCalendarTasks(response.data);
    } catch (error) {
      console.error('Error fetching calendar tasks:', error);
    }
  };

  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    fetchCalendarTasks(year, month);
  }, [currentDate]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getTasksForDate = (day) => {
    if (!day) return [];
    
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarTasks.filter(task => 
      task.due_date && task.due_date.startsWith(dateStr)
    );
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedTasks([]);
  };

  const selectDate = (day) => {
    if (!day) return;
    
    const tasksForDay = getTasksForDate(day);
    setSelectedDate(day);
    setSelectedTasks(tasksForDay);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'meeting': return '👥';
      case 'followup': return '📞';
      case 'directive': return '📋';
      case 'email': return '📧';
      case 'note': return '📝';
      case 'other': return '📌';
      default: return '📌';
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return day &&
           today.getDate() === day &&
           today.getMonth() === currentDate.getMonth() &&
           today.getFullYear() === currentDate.getFullYear();
  };

  const days = getDaysInMonth(currentDate);

  return (
    <Layout>
      <div className="fade-in">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('calendar')}
          </h1>
          <p className="text-gray-600">
            عرض المهام والأحداث حسب التاريخ
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="card-header">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="btn btn-outline btn-sm"
                  >
                    ← الشهر السابق
                  </button>
                  <h2 className="text-xl font-bold">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="btn btn-outline btn-sm"
                  >
                    الشهر التالي →
                  </button>
                </div>
              </div>
              <div className="card-body p-0">
                {/* Days of week header */}
                <div className="grid grid-cols-7 border-b">
                  {dayNames.map((dayName) => (
                    <div key={dayName} className="p-3 text-center font-semibold text-gray-700 border-r last:border-r-0">
                      {dayName}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7">
                  {days.map((day, index) => {
                    const tasksForDay = getTasksForDate(day);
                    const isSelectedDate = selectedDate === day;
                    const isTodayDate = isToday(day);
                    
                    return (
                      <div
                        key={index}
                        className={`
                          min-h-[100px] p-2 border-r border-b last:border-r-0 cursor-pointer transition-colors
                          ${day ? 'hover:bg-gray-50' : ''}
                          ${isSelectedDate ? 'bg-blue-50 border-blue-300' : ''}
                          ${isTodayDate ? 'bg-maroon-50 border-maroon-300' : ''}
                        `}
                        onClick={() => selectDate(day)}
                      >
                        {day && (
                          <>
                            <div className={`
                              text-sm font-semibold mb-1
                              ${isTodayDate ? 'text-white bg-maroon rounded-full w-6 h-6 flex items-center justify-center' : ''}
                            `}>
                              {day}
                            </div>
                            <div className="space-y-1">
                              {tasksForDay.slice(0, 3).map((task) => (
                                <div
                                  key={task.id}
                                  className={`
                                    text-xs p-1 rounded text-white truncate
                                    ${getPriorityColor(task.priority)}
                                  `}
                                  title={task.title}
                                >
                                  {getCategoryIcon(task.category)} {task.title}
                                </div>
                              ))}
                              {tasksForDay.length > 3 && (
                                <div className="text-xs text-gray-500 font-semibold">
                                  +{tasksForDay.length - 3} أخرى
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Selected Date Tasks */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  {selectedDate 
                    ? `مهام يوم ${selectedDate} ${monthNames[currentDate.getMonth()]}` 
                    : 'اختر تاريخاً لعرض المهام'
                  }
                </h3>
              </div>
              <div className="card-body">
                {!selectedDate ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">📅</div>
                    <p className="text-gray-600">انقر على أي تاريخ في التقويم لعرض مهام ذلك اليوم</p>
                  </div>
                ) : selectedTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">✨</div>
                    <p className="text-gray-600">لا توجد مهام مجدولة لهذا اليوم</p>
                    <a href="/tasks" className="btn btn-primary mt-4">
                      إضافة مهمة جديدة
                    </a>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedTasks.map((task) => (
                      <div key={task.id} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getCategoryIcon(task.category)}</span>
                            <h4 className="font-semibold text-sm">{task.title}</h4>
                          </div>
                          <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`}></div>
                        </div>
                        
                        {task.description && (
                          <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                        )}
                        
                        <div className="flex justify-between items-center text-xs">
                          <span className={`px-2 py-1 rounded-full ${
                            task.status === 'completed' ? 'bg-green-100 text-green-800' :
                            task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {t(task.status)}
                          </span>
                          <span className="text-gray-500">
                            {t(task.priority)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="card mt-4">
              <div className="card-body">
                <h4 className="font-semibold mb-3">إحصائيات الشهر</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>إجمالي المهام:</span>
                    <span className="font-semibold">{calendarTasks.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>المهام المكتملة:</span>
                    <span className="font-semibold text-green-600">
                      {calendarTasks.filter(t => t.status === 'completed').length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>المهام المعلقة:</span>
                    <span className="font-semibold text-yellow-600">
                      {calendarTasks.filter(t => t.status === 'pending').length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>المهام عالية الأولوية:</span>
                    <span className="font-semibold text-red-600">
                      {calendarTasks.filter(t => t.priority === 'high').length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Calendar;